import React, { useState, useEffect, useCallback } from "react";
import { Plus, X, ListPlus, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OptionManager = ({ title, icon: Icon, kind, values, adminKey, onChange }) => {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const call = async (path, value) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Key": adminKey },
        body: JSON.stringify({ kind, value }),
      });
      const data = await r.json();
      if (r.ok) onChange(data);
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    await call("/stock/options", input.trim());
    setInput("");
  };

  return (
    <div className="rounded-2xl border bg-white p-6" style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
      <h3 className="flex items-center gap-2 font-heading text-[17px] font-bold" style={{ color: "var(--green-950)" }}>
        <Icon size={18} style={{ color: "#E8501A" }} /> {title}
      </h3>
      <div className="mt-4 flex flex-wrap gap-2">
        {values.map((v) => (
          <span key={v} data-testid={`option-chip-${kind}-${v.replace(/\s/g, "-").toLowerCase()}`}
                className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-heading text-[13px] font-bold"
                style={{ borderColor: "var(--line)", color: "var(--green-900)", background: "var(--cream)" }}>
            {v}
            <button onClick={() => call("/stock/options/remove", v)} disabled={busy} aria-label={`Remove ${v}`}
                    data-testid={`option-remove-${kind}-${v.replace(/\s/g, "-").toLowerCase()}`}
                    className="grid h-4.5 w-4.5 place-items-center rounded-full transition-colors hover:text-white"
                    style={{ color: "var(--orange-700)" }}><X size={13} /></button>
          </span>
        ))}
        {values.length === 0 && <span className="text-[13.5px]" style={{ color: "var(--muted)" }}>No options yet.</span>}
      </div>
      <form onSubmit={add} className="mt-4 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={`New ${title.toLowerCase().replace(/s$/, "")}…`}
               data-testid={`option-input-${kind}`}
               className="w-full rounded-lg border bg-white px-3.5 py-2.5 text-[14px] outline-none focus:ring-4"
               style={{ borderColor: "#d8c8af", color: "var(--green-950)" }} />
        <button type="submit" disabled={busy || !input.trim()} data-testid={`option-add-${kind}`}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-4 font-heading text-[13px] font-bold text-white disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
          <Plus size={15} /> Add
        </button>
      </form>
    </div>
  );
};

export default function AdminSettings({ adminKey }) {
  const [opts, setOpts] = useState({ item_types: [], enquiry_sources: [] });

  const load = useCallback(() => {
    fetch(`${API}/stock/options`).then((r) => r.json()).then(setOpts).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="admin-settings">
      <OptionManager title="Item Types" icon={ListPlus} kind="item_type"
                     values={opts.item_types} adminKey={adminKey} onChange={setOpts} />
      <OptionManager title="Enquiry Sources" icon={Megaphone} kind="enquiry_source"
                     values={opts.enquiry_sources} adminKey={adminKey} onChange={setOpts} />
      <p className="text-[13px] lg:col-span-2" style={{ color: "var(--muted)" }}>
        These options appear in the dropdowns on the mobile Stock Entry page (/warehouse) and the Admin Upload page.
      </p>
    </div>
  );
}
